/**
 * @module app.um
 */

import { autoinject, bindingMode, observable } from 'aurelia-framework';
import { bindable } from 'aurelia-typed-observable-plugin';
import { apiCall } from 'app/shared/lib/api/api';
import { debounce } from 'app/shared/lib/decorators';
import { IQueryLoadOptions } from 'app/shared/lib/api/api-types';
import { ComponentBase, Sorting, SortDirection } from 'app/shared/lib/essette-site';
import { SortChangeEvent } from 'app/shared/attributes/sortable-attribute';
import { EssPagerCustomElement } from 'app/shared/elements/ess-pager';
import { EssTableCustomElement, ExpandEvent } from 'app/shared/elements/ess-table';
import { ICustomFieldTargetColumnsContainer } from 'app/shared/lib/custom-field-definitions'
import { CustomFieldContext } from 'app/core/lib/custom-field-context';
import { openAuthSummary } from 'app/um/components/summary/auth-summary';
import { AuthService, IAuthSearchFilters } from 'app/um/services/auth-service';
import { IMemberSearchFilters } from 'app/cd/services/member-service';
import { IAuthContextSettingsDetails } from 'app/um/lib/auth-context';
import { IServiceGridOptions } from 'app/um/components/auth/service-grid';
import { IAuthMedicationsGridOptions } from 'app/um/components/auth/medications/auth-medications-grid';
import * as UM from 'app/um/models/essette-api';

@autoinject()
export class MemberUtilizationManagement extends ComponentBase {

    ////****************** Fields and Properties ************************************////
    @bindable.stringNull({ defaultBindingMode: bindingMode.toView, changeHandler: 'reload' })
    public memberId: string | null = null;
    protected auths: ReadonlyArray<UM.Auth> = [];

    protected showAuthSummaryLink: boolean = false;
    protected openAuthSummary = openAuthSummary;
    protected showAuthStatus: boolean = false;
    protected hasAuthRoute = false;
    protected showAuthDiagsServices: boolean = false;

    protected drugApprovedCharges: IAuthContextSettingsDetails = { visible: true };
    protected drugRequestedCharges: IAuthContextSettingsDetails = { visible: true };
    protected drugRequestedDays: IAuthContextSettingsDetails = { visible: true };
    protected drugsStatusReason: IAuthContextSettingsDetails = { visible: true };
    protected servicesAuthDate: IAuthContextSettingsDetails = { visible: true };
    protected servicesExpDate: IAuthContextSettingsDetails = { visible: true };
    protected serviceRequestedCharges: IAuthContextSettingsDetails = { visible: true };
    protected serviceApprovedCharges: IAuthContextSettingsDetails = { visible: true };
    protected servicesStatusReason: IAuthContextSettingsDetails = { visible: true };
    protected servicesModifier: IAuthContextSettingsDetails = { visible: true };
    protected servicesDental: IAuthContextSettingsDetails = { visible: true };
    protected servicesDentalAuthDate: IAuthContextSettingsDetails = { visible: true };
    protected servicesDentalExpDate: IAuthContextSettingsDetails = { visible: true };
    protected servicesDentalStatusReason: IAuthContextSettingsDetails = { visible: true };
    protected servicesDentalModifier: IAuthContextSettingsDetails = { visible: true };

    protected showExternalActionsForGrids: boolean = true;
    protected serviceGridOptions: IServiceGridOptions | null = null;
    protected authMedicationsGridOptions: IAuthMedicationsGridOptions | null = null;
    protected drugsAuthDate: IAuthContextSettingsDetails = { visible: true };
    protected drugsExpDate: IAuthContextSettingsDetails = { visible: true };

    protected isProviderPortal: boolean = false;

    protected authDetails: Record<string, boolean> = {};

    @observable
    protected filterText: string = '';
    protected authSearchFilters: IAuthSearchFilters = {}
    protected memberSearchFilters: IMemberSearchFilters = {};
    protected minSearchLength: number = 3;
    protected maxSearchLength: number = 200;

    protected customFieldTargetColumns: ICustomFieldTargetColumnsContainer | null = null;

    private sorting: Sorting = new Sorting();
    protected resultCount: number = 0;
    protected pageSize: number = 25;

    ////****************** Element References ***************************************////
    protected $pager!: EssPagerCustomElement;
    protected $table!: EssTableCustomElement<UM.Auth>;

    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly authService: AuthService,
        private readonly customFieldContext: CustomFieldContext,
        ...rest: any[]) {
        super(...rest);
        // If Application is Provider Portal, do not display external actions (IQC, CWQI) and limit certain fields
        this.isProviderPortal = this.env.application.code === 'Portal.Providers';
        this.showExternalActionsForGrids = !this.isProviderPortal;
        this.serviceGridOptions = {
            canOpenServiceStatusHistory: !this.isProviderPortal,
            canSyncToClaimsSystems: !this.isProviderPortal,
            showDueDate: !this.isProviderPortal,
            showServiceStatusReason: this.isProviderPortal,
            canApplyBulkServiceStatus: !this.isProviderPortal,
            canApplyCorrespondenceRules: !this.isProviderPortal,
            showExternalActions: !this.isProviderPortal,
            showApprovedWithRequestedCharges: this.isProviderPortal,
            canSyncToClaimsSystemsLineLevel: !this.isProviderPortal,
            showExistingAuthsWarning: !this.isProviderPortal,
            showDuplicateAuths: false,
            showAddDocumentButton: false,
            showOpenIqcSummary: true,
            showWarnings: false,
            showModifiers: true
        };
        this.authMedicationsGridOptions = {
            showStatusReason: this.isProviderPortal,
            showQtyApproved: !this.isProviderPortal,
            showDaysApproved: !this.isProviderPortal,
            showApprovedWithRequestedCharges: this.isProviderPortal,
            showWarnings: false
        };
    }

    protected bind(): void {
        this.memberSearchFilters.id = this.memberId || null;
        this.checkPermissions();
        this.loadCustomFieldTargets();
    }

    ////****************** Events ***************************************************////
    protected authDetailLoaded(event: ExpandEvent<UM.Auth>): void {
        const item = event.detail.item;
        const items = event.detail.items;

        if(item && items.includes(item)) {
            this.authDetails[item.id!] = true;
        }
    }

    ////****************** Functions ************************************************////
    protected async applySort(event: SortChangeEvent): Promise<void> {
        const { property, direction } = event.detail;
        const { sorting } = this;
        if(direction === null) {
            sorting.remove(property);
        } else {
            sorting.add({
                reference: property,
                direction: direction === 'asc' ? SortDirection.Ascending : SortDirection.Descending
            });
        }
        await this.reload();
    }

    public async reload(): Promise<void> {
        if(!this.memberId) return;
        this.authDetails = {};
        if(this.authSearchFilters.detailFilter) {
            await this.searchAuths();
            // expand all details when filter is used
            this.auths.forEach(a => this.authDetails[a.id!] = true);
            this.$table.openAll();
        } else {
            await this.loadAuths();
        }
    }

    private async checkPermissions(): Promise<void> {
        try {
            this.state.beginLoad();
            await this.accountContext.load();
        } catch(error) {
            throw error;
        } finally {
            this.showAuthSummaryLink = this.accountContext.hasRouteAccess('um.auth.summary');
            this.showAuthDiagsServices = this.accountContext.hasRouteAccess('um.auth.diags-services');
            this.showAuthStatus = this.accountContext.hasRouteAccess('um.auth.status');
            this.hasAuthRoute = this.accountContext.hasRouteAccess('um.auth');
            this.state.endLoad();

            await this.reload();
        }
    }

    @debounce(600)
    protected async filterTextChanged(): Promise<void> {
        const search = this.filterText;
        this.authDetails = {};

        // make sure minimum search length criteria is met
        if(search && search.length >= this.minSearchLength && this.memberSearchFilters.id) {
            await this.searchAuths();
            // expand all details when filter is used
            this.auths.forEach(a => this.authDetails[a.id!] = true);
            this.$table.openAll();
        }
        // otherwise load unfiltered results and clear any potential previous filters
        else if(!search && this.authSearchFilters.detailFilter) {
            this.clearResults();
            this.authSearchFilters.detailFilter = null;
            await this.loadAuths();
        }
    }

    protected clearResults(): void {
        this.auths = [];
        this.resultCount = 0;
        if(this.$pager) this.$pager.updatePage(0);
    }

    ////****************** Service Calls ********************************************////
    private async loadCustomFieldTargets(): Promise<void> {
        this.customFieldTargetColumns = await this.customFieldContext.getCustomFieldTargetColumns('UM.Auth', { definitionCount: 10 });
    }

    @apiCall
    private async searchAuths(): Promise<void> {
        const options: Partial<IQueryLoadOptions> = {
            includeInactive: false,
            top: this.pageSize,
            sorting: this.sorting,
            page: this.$pager?.page || 0
        };

        const includes: string[] = [
            'assignedTo',
            'class',
            'class.subClass',
            'diagnoses',
            'drugs',
            'externalSystem',
            'healthcareServiceType',
            'metadata.created.byUser',
            'placeOfService',
            'priority',
            'services',
            'services.definition.type.bodySiteType',
            'type'
        ];

        const fields = [
            'admission.date',
            'assignedTo.description',
            'class.description',
            'class.subClass.description',
            'custom.lookup.*',
            'custom.text.*',
            'diagnoses.code',
            'diagnoses.description',
            'drugs.code',
            'drugs.description',
            'dueDateTime',
            'externalSystem.code',
            'isAdmission',
            'isClosed',
            'metadata.created.byUser.description',
            'number',
            'priority.description',
            'requestedDateTime',
            'services.code',
            'services.description',
            'services.definition.type.bodySiteType.code',
            'serviceDate.dateRange.end',
            'serviceDate.dateRange.start',
            'type.description'
        ];

        this.authSearchFilters.detailFilter = this.filterText;

        try {
            this.state.beginLoad();
            if(this.showAuthStatus) {
                includes.push('status', 'status.reason');
                fields.push('status.description', 'status.reason.description');
            }

            const response = await this.authService.searchAuths(this.authSearchFilters, this.memberSearchFilters, options, includes, fields);
            this.auths = response.results;
            this.resultCount = response.count || 0;
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

    @apiCall
    private async loadAuths(): Promise<void> {
        if(!this.memberId) return;

        const options: Partial<IQueryLoadOptions> = {
            top: this.pageSize,
            page: this.$pager?.page || 0,
            sorting: this.sorting
        };

        const includes = [
            'assignedTo',
            'class',
            'class.subClass',
            'custom',
            'diagnoses',
            'drugs',
            'externalSystem',
            'healthcareServiceType',
            'metadata.created.byUser',
            'placeOfService',
            'priority',
            'services',
            'services.definition.type.bodySiteType',
            'type'
        ];

        const fields = [
            'admission.date',
            'assignedTo.description',
            'class.description',
            'class.subClass.description',
            'custom.lookup.*',
            'custom.text.*',
            'dueDateTime',
            'externalSystem.code',
            'healthcareServiceType.description',
            'isAdmission',
            'isClosed',
            'metadata.created.byUser.description',
            'number',
            'placeOfService.description',
            'priority.description',
            'requestedDateTime',
            'services.definition.type.bodySiteType.code',
            'serviceDate.dateRange.end',
            'serviceDate.dateRange.start',
            'type.description'
        ];

        try {
            this.state.beginLoad();
            if(this.showAuthStatus) {
                includes.push('status', 'status.reason');
                fields.push('status.description', 'status.reason.description');
            }

            const response = await this.authService.loadAuthsByMember(this.memberId, options, includes, fields);
            this.auths = response.results;
            this.resultCount = response.count || 0;
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }
}
